import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMe } from "@/auth/useMe";
import { EmptyState, Page } from "@/components/ui";
import { DTMF_KEYS } from "@/telephony/dtmf";
import { useSoftphone } from "@/telephony/SoftphoneProvider";
import { canSendTones, useSoftphoneStore } from "@/telephony/store";

/**
 * *Llamar* — a keypad, for a number nobody has on file.
 *
 * Every other call in the CRM starts from a record: a contact's phone, a lead's.
 * This one starts from ten digits off a directory or a truck's tailgate, which
 * is what prospecting is. So the screen is the keypad and nothing else — there
 * is no company to show yet. What the call becomes is decided when it ends, in
 * the wrap-up (`CallProspect`): a company, its branch and a contact, or nothing,
 * in which case it stays a *Llamada de prospección* with nobody attached and
 * still counts in *Actividades* as work the agent did.
 *
 * Once the call is answered the same keys stop writing a number and start
 * sending tones, `*` and `#` included, the way a handset's do: the number at
 * the other end is very often a switchboard asking for an extension.
 *
 * Open to every role, on one condition the server enforces too: the agent has a
 * number of their own. A stranger who calls back has to reach whoever rang
 * them, and the shared line forwards to nobody.
 */

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0"] as const;

/** A `+` and fifteen digits is the longest number E.164 allows. */
const MAX_DIGITS = 15;

/** Digits, and a `+` only where it means something: first. */
function clean(raw: string): string {
  const plus = raw.trimStart().startsWith("+") ? "+" : "";
  return plus + raw.replace(/\D/g, "").slice(0, MAX_DIGITS);
}

/**
 * Ten digits are a Mexican number; anything else says its country with a `+`.
 * A first pass only — the server parses it properly and is the one that
 * refuses (`StartCallSerializer`).
 */
function isDialable(number: string): boolean {
  return /^\d{10}$/.test(number) || /^\+\d{8,15}$/.test(number);
}

export function Llamar() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { start, isStarting, sendDigits } = useSoftphone();
  const active = useSoftphoneStore((state) => state.call);
  const [number, setNumber] = useState("");
  const form = useRef<HTMLFormElement>(null);

  const assigned = me?.profile?.twilio_caller_id ?? "";
  // One call at a time, as on every other dial button: the `Device` holds a
  // single leg.
  const busy = isStarting || active !== null;
  // Answered, on the softphone: the pad is the call's now, not the next one's.
  const tones = canSendTones(active);

  async function dial(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !isDialable(number)) return;
    await start({ number, keypad: true });
    // `start` reports a refusal on the call bar and resolves either way. The
    // number stays on a failure, so a typo is fixed and not retyped.
    if (useSoftphoneStore.getState().call) setNumber("");
  }

  // The computer's own keys, wherever the focus is. The display is a real
  // input and types by itself once clicked, but nobody clicks a field to use a
  // keypad — and a click on one of the keys below would take the focus away
  // from it again. So digits, `+`, Backspace and Enter are heard on the page.
  const canDial = assigned !== "";
  useEffect(() => {
    if (!canDial) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      // On an answered call the keys are tones, and `SoftphoneProvider` is the
      // one that sends them — from every screen, this one included.
      if (canSendTones(useSoftphoneStore.getState().call)) return;
      const target = event.target instanceof Element ? event.target : null;
      // Somebody typing somewhere else — the write-up in the call bar, a modal
      // over the page — is not dialling.
      if (
        target?.closest("input, textarea, select, [contenteditable='true']") ||
        document.querySelector("dialog[open]")
      ) {
        return;
      }
      if (/^\d$/.test(event.key) || event.key === "+") {
        setNumber((previous) => clean(previous + event.key));
      } else if (event.key === "Backspace") {
        setNumber((previous) => previous.slice(0, -1));
      } else if (event.key === "Enter" && !target?.closest("button, a")) {
        // On a button or a link Enter is that control's own; anywhere else it
        // is the call, checked by the form like a click on *Llamar*.
        form.current?.requestSubmit();
      } else {
        return;
      }
      event.preventDefault();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canDial]);

  return (
    <Page title={t("dialer.title")}>
      {canDial ? (
        <form
          ref={form}
          onSubmit={(event) => void dial(event)}
          className="mx-auto flex w-full max-w-[320px] flex-col gap-4"
        >
          <div>
            <label htmlFor="dialer-number" className="sr-only">
              {t(tones ? "dialer.tones" : "dialer.number")}
            </label>
            {tones ? (
              // Not a field: there is nothing to edit in a tone already sent,
              // and a focused input would swallow the next key instead of
              // letting it reach the call.
              <output
                id="dialer-number"
                className="input flex h-14 items-center justify-center overflow-hidden font-display text-2xl font-extrabold tabular-nums"
              >
                {active.digits}
              </output>
            ) : (
              <input
                id="dialer-number"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                value={number}
                placeholder={t("dialer.placeholder")}
                onChange={(event) => setNumber(clean(event.target.value))}
                className="input h-14 text-center font-display text-2xl font-extrabold tabular-nums"
              />
            )}
            <p className="mt-1.5 text-center text-[11px] text-mute-600">
              {t(tones ? "dialer.tonesHint" : "dialer.hint")}
            </p>
          </div>

          <div
            role="group"
            aria-label={t("dialer.keypad")}
            className="grid grid-cols-3 gap-2"
          >
            {(tones ? DTMF_KEYS : KEYS).map((key) => (
              <button
                key={key}
                type="button"
                // A `+` anywhere but first is not a number, so `clean` drops
                // it; the key is only live on an empty display.
                disabled={!tones && key === "+" && number !== ""}
                // The mouse does not take the focus: left on the key, the next
                // Enter would press it again instead of placing the call.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() =>
                  tones
                    ? sendDigits(key)
                    : setNumber((previous) => clean(previous + key))
                }
                className="btn btn-secondary h-16 justify-center font-display text-2xl font-extrabold"
              >
                {key}
              </button>
            ))}
            {/* `#` has this cell during a call: a tone that went down the line
                cannot be taken back, so there is nothing to erase. */}
            {tones ? null : (
              <button
                type="button"
                aria-label={t("dialer.delete")}
                disabled={number === ""}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setNumber((previous) => previous.slice(0, -1))}
                className="btn btn-ghost h-16 justify-center"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 5H9l-6 7 6 7h12zM17 9l-5 6M12 9l5 6" />
                </svg>
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={busy || !isDialable(number)}
            className="btn btn-primary btn-block h-14 justify-center text-base"
          >
            {t("call.start")}
          </button>

          <p className="text-center text-[11px] text-mute-600">
            {t("dialer.from", { number: assigned })}
          </p>
        </form>
      ) : (
        // Said here, before a number is typed: the server refuses the call for
        // the same reason, and that is a worse place to learn it.
        <EmptyState>{t("dialer.noNumber")}</EmptyState>
      )}
    </Page>
  );
}
